import React, { useState, useEffect, useMemo } from 'react';
import './style.scss';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultButton from 'components/Utils/NewDefaultButton';
import ProgressMessage from 'components/Utils/ProgressMessage';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import PerformanceUtils from 'services/PerformanceUtils';
import { DefaultSwitch } from 'services/UIUtils';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import MenuItem from '@material-ui/core/MenuItem';
import PhysicianUtils from 'services/PhysicianUtils';
import { findIconDefinition } from '@fortawesome/fontawesome-svg-core';

// Ops-only plan configuration. Booking access is gated by the delivery-option
// "subscribers only" flag, per-concern fees, and the "Unlocks practitioner
// skills" list below (a practitioner carrying a skill some plan unlocks can
// only be booked by patients on a plan that unlocks it). Beyond that gate this
// screen controls how plans are presented (visibility, card bullets, badge) and
// the minimum commitment that drives the contract-signing featureset.
const VISIBILITY_OPTIONS = [
  { value: 'public', label: 'Public (all patients)' },
  { value: 'internal', label: 'Internal (staff/test accounts only)' },
  { value: 'hidden', label: 'Hidden' },
];

const MAX_FEATURES = 12;
const MAX_MARKETING_TILES = 8;

// Duotone icons are registered individually in src/index.jsx rather than pulled
// in as a whole style, so a name that is not on that list renders as nothing on
// the patient's screen. Checking against the live library here makes a typo
// visible while it is being typed instead of silently blank in production.
function isRegisteredIcon(iconName) {
  const name = (iconName || '').trim();
  if (!name) return true;
  return !!findIconDefinition({ prefix: 'fad', iconName: name });
}

// The backend reads `marketing: undefined` as "leave it alone" and rejects
// blank strings inside a marketing block, so an editor that is entirely empty
// has to send undefined rather than `{}` or a block full of empty strings.
function trimmed(value) {
  const text = (value || '').trim();
  return text ? text : undefined;
}

function sanitizeMarketing(marketing) {
  if (!marketing) return undefined;

  const tiles = (marketing.tiles || [])
    .map((tile) => ({
      title: trimmed(tile.title),
      body: trimmed(tile.body),
      icon: trimmed(tile.icon),
    }))
    // A tile without a title is not a tile the backend will accept, and an
    // otherwise-empty row is just an unfilled one the ops user left behind.
    .filter((tile) => !!tile.title);

  const cleaned = {
    headline: trimmed(marketing.headline),
    subhead: trimmed(marketing.subhead),
    intro: trimmed(marketing.intro),
    includes: trimmed(marketing.includes),
    tiles: tiles.length ? tiles : undefined,
  };

  const hasContent = Object.values(cleaned).some((value) => value !== undefined);
  return hasContent ? cleaned : undefined;
}

const PaymentPlanSetup = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState([]);

  const dispatch = useAppDispatch();

  useEffect(() => {
    setLoading(true);
    dispatch(api.Payments.getAllPaymentPlans()).then((result) => {
      setLoading(false);
      if (result) setPlans(result);
    });
  }, [dispatch]);

  function onChange(planId, field, value) {
    let modified = PerformanceUtils.clone(plans).map((plan) => {
      if (plan.id === planId) {
        plan[field] = value;
      }
      return plan;
    });
    setPlans(modified);
  }

  async function savePaymentPlans() {
    let clinicPaymentPlans = PerformanceUtils.clone(plans);
    setSaving(true);

    clinicPaymentPlans = clinicPaymentPlans.map((plan) => ({
      id: plan.id,
      visibility: plan.visibility,
      highlight: plan.highlight,
      minimum_months: plan.minimum_months,
      features: (plan.features || []).filter((feature) => !!feature.trim()),
      unlocks_skills: plan.unlocks_skills || [],
      closed_to_new_members: !!plan.closed_to_new_members,
      closed_message: trimmed(plan.closed_message),
      marketing: sanitizeMarketing(plan.marketing),
    }));

    const result = await dispatch(api.Payments.editPaymentPlans({ clinicPaymentPlans }), {
      title: 'Payment Plans',
      successText: 'Payment plans saved!',
    });
    setSaving(false);
    if (result) setPlans(result);
  }

  const archived = useMemo(() => {
    return plans.filter((plan) => !plan.payment_plan.active);
  }, [plans]);

  const active = useMemo(() => {
    return plans.filter((plan) => !!plan.payment_plan.active);
  }, [plans]);

  return (
    <div className="payment-plan-setup full-width">
      <ProgressMessage show={loading && plans.length === 0} />
      {active.length > 0 && <span className="group-title">Active</span>}
      {active.map((plan) => (
        <Plan key={plan.id} plan={plan} onChange={onChange} />
      ))}
      {archived.length > 0 && <span className="group-title">Archived</span>}
      {archived.map((plan) => (
        <Plan key={plan.id} plan={plan} onChange={onChange} />
      ))}
      <div className="row save-row">
        <DefaultButton name="Save" className="blue" onClick={savePaymentPlans} loading={saving} />
      </div>
    </div>
  );
};

const Plan = ({ plan, onChange }) => {
  const isMonthly = plan.payment_plan.interval === 'month';
  const features = plan.features || [];
  const [showMarketing, setShowMarketing] = useState(false);

  return (
    <div className="plan">
      <span className="plan-name">
        {plan.payment_plan.name} - {plan.payment_plan.interval || 'pay-per-use'}
      </span>
      <div className="row permissions">
        <DefaultSelectField
          name={plan.id + '-visibility'}
          label="Visibility"
          value={plan.visibility || 'hidden'}
          onChange={(event) => onChange(plan.id, 'visibility', event.target.value)}
          style={{ width: '250px' }}
          inputElements={VISIBILITY_OPTIONS.map((option) => (
            <MenuItem value={option.value} key={plan.id + '-visibility-' + option.value}>
              {option.label}
            </MenuItem>
          ))}
        />
        <DefaultSwitch
          name={plan.id + '-highlight'}
          label="Highlight (most popular)"
          checked={!!plan.highlight}
          onChange={(event, checked) => onChange(plan.id, 'highlight', checked)}
        />
        {isMonthly && (
          <div className="minimum-months-field">
            <DefaultTextField
              label="Minimum commitment (months)"
              name={plan.id + '-minimum-months'}
              type="number"
              value={plan.minimum_months ?? ''}
              onChange={(event) =>
                onChange(
                  plan.id,
                  'minimum_months',
                  event.target.value === '' ? undefined : parseInt(event.target.value),
                )
              }
              style={{ width: '220px' }}
            />
            <span className="field-hint">
              Applies to monthly subscriptions. Leave empty to disable the commitment period and
              contract signing for this plan.
            </span>
          </div>
        )}
      </div>
      <div className="row permissions closed-row">
        <DefaultSwitch
          name={plan.id + '-closed'}
          label="Closed to new members"
          checked={!!plan.closed_to_new_members}
          onChange={(event, checked) => onChange(plan.id, 'closed_to_new_members', checked)}
        />
        {!!plan.closed_to_new_members && (
          <div className="closed-message-field">
            <DefaultTextField
              label="Message shown on the plan card"
              name={plan.id + '-closed-message'}
              value={plan.closed_message || ''}
              onChange={(event) => onChange(plan.id, 'closed_message', event.target.value)}
              htmlInputProps={{ maxLength: 200 }}
              style={{ width: '400px' }}
            />
            <span className="field-hint">
              The card stays on the subscribe screen but can't be selected. Members already on this
              plan keep it and can still switch between monthly and annual. Leave the message empty
              for "We're not accepting new {plan.payment_plan.name} members at the moment."
            </span>
          </div>
        )}
      </div>
      <div className="features-editor">
        <span className="features-title">Plan card bullets</span>
        {features.map((feature, index) => (
          <div className="row feature-row" key={plan.id + '-feature-' + index}>
            <DefaultTextField
              name={plan.id + '-feature-' + index}
              value={feature}
              onChange={(event) => {
                const updated = [...features];
                updated[index] = event.target.value;
                onChange(plan.id, 'features', updated);
              }}
              style={{ width: '400px' }}
            />
            <DefaultButton
              name="Remove"
              className="red"
              onClick={() =>
                onChange(
                  plan.id,
                  'features',
                  features.filter((_, i) => i !== index),
                )
              }
            />
          </div>
        ))}
        <DefaultButton
          name="Add Bullet"
          disabled={features.length >= MAX_FEATURES}
          onClick={() => onChange(plan.id, 'features', [...features, ''])}
        />
      </div>
      <div className="row permissions unlocks-skills-row">
        <DefaultSelectField
          name={plan.id + '-unlocks-skills'}
          label="Unlocks practitioner skills"
          value={plan.unlocks_skills || []}
          multiple={true}
          onChange={(event) => onChange(plan.id, 'unlocks_skills', event.target.value)}
          style={{ width: '250px' }}
          inputs={Object.values(PhysicianUtils.PHYSICIAN_SKILLS)}
        />
        <span className="field-hint">
          Practitioners with any of these skills can only be booked by patients on a plan that
          unlocks it. Everyone else sees their times locked with an upgrade prompt.
        </span>
      </div>
      <MarketingEditor
        plan={plan}
        expanded={showMarketing}
        onToggle={() => setShowMarketing((expanded) => !expanded)}
        onChange={onChange}
      />
    </div>
  );
};

// Copy for the subscribe intro screen. Everything here is optional: a plan with
// no headline renders nothing at all on that screen, so tenants that never open
// this section are unchanged.
const MarketingEditor = ({ plan, expanded, onToggle, onChange }) => {
  const marketing = plan.marketing || {};
  const tiles = marketing.tiles || [];

  function onMarketingChange(field, value) {
    onChange(plan.id, 'marketing', { ...marketing, [field]: value });
  }

  function onTileChange(index, field, value) {
    const updated = tiles.map((tile, i) => (i === index ? { ...tile, [field]: value } : tile));
    onMarketingChange('tiles', updated);
  }

  return (
    <div className="marketing-editor">
      <div className="row marketing-header" onClick={onToggle}>
        <span className="features-title">Intro screen marketing</span>
        <span className="toggle">{expanded ? 'Hide' : 'Edit'}</span>
      </div>
      {expanded && (
        <>
          <span className="field-hint">
            Shown on the subscribe intro screen, under the standard perks. Leave every field empty
            to show nothing for this plan.
          </span>
          <div className="row marketing-fields">
            <DefaultTextField
              label="Headline"
              name={plan.id + '-marketing-headline'}
              value={marketing.headline || ''}
              onChange={(event) => onMarketingChange('headline', event.target.value)}
              htmlInputProps={{ maxLength: 80 }}
              style={{ width: '400px' }}
            />
            <DefaultTextField
              label="Subhead"
              name={plan.id + '-marketing-subhead'}
              value={marketing.subhead || ''}
              onChange={(event) => onMarketingChange('subhead', event.target.value)}
              htmlInputProps={{ maxLength: 160 }}
              style={{ width: '400px' }}
            />
          </div>
          <DefaultTextField
            label="Intro paragraph"
            name={plan.id + '-marketing-intro'}
            value={marketing.intro || ''}
            onChange={(event) => onMarketingChange('intro', event.target.value)}
            multiline={true}
            maxRows={6}
            htmlInputProps={{ maxLength: 600 }}
            style={{ width: '100%', maxWidth: '820px', marginTop: '10px' }}
          />
          <DefaultTextField
            label="Includes line"
            name={plan.id + '-marketing-includes'}
            value={marketing.includes || ''}
            onChange={(event) => onMarketingChange('includes', event.target.value)}
            htmlInputProps={{ maxLength: 120 }}
            style={{ width: '100%', maxWidth: '820px', marginTop: '10px' }}
          />
          <div className="marketing-tiles">
            <span className="features-title">Tiles</span>
            <span className="field-hint">
              Icon is a duotone FontAwesome name that the app registers, e.g. user-doctor,
              stethoscope, calendar-check, calendar-heart, video, files-medical, flask,
              prescription, heart-pulse, message, circle-check. Anything else renders as nothing;
              blank falls back to calendar-check.
            </span>
            {tiles.map((tile, index) => (
              <div className="row tile-row" key={plan.id + '-marketing-tile-' + index}>
                <DefaultTextField
                  label="Title"
                  name={plan.id + '-marketing-tile-title-' + index}
                  value={tile.title || ''}
                  onChange={(event) => onTileChange(index, 'title', event.target.value)}
                  htmlInputProps={{ maxLength: 60 }}
                  style={{ width: '220px' }}
                />
                <DefaultTextField
                  label="Body"
                  name={plan.id + '-marketing-tile-body-' + index}
                  value={tile.body || ''}
                  onChange={(event) => onTileChange(index, 'body', event.target.value)}
                  multiline={true}
                  maxRows={4}
                  htmlInputProps={{ maxLength: 200 }}
                  style={{ width: '340px' }}
                />
                <div className="icon-field">
                  <DefaultTextField
                    label="Icon"
                    name={plan.id + '-marketing-tile-icon-' + index}
                    value={tile.icon || ''}
                    onChange={(event) => onTileChange(index, 'icon', event.target.value)}
                    htmlInputProps={{ maxLength: 40 }}
                    style={{ width: '150px' }}
                  />
                  {!isRegisteredIcon(tile.icon) && (
                    <span className="icon-warning">Icon not available</span>
                  )}
                </div>
                <DefaultButton
                  name="Remove"
                  className="red"
                  onClick={() =>
                    onMarketingChange(
                      'tiles',
                      tiles.filter((_, i) => i !== index),
                    )
                  }
                />
              </div>
            ))}
            <DefaultButton
              name="Add Tile"
              disabled={tiles.length >= MAX_MARKETING_TILES}
              onClick={() => onMarketingChange('tiles', [...tiles, { title: '' }])}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default React.memo(PaymentPlanSetup);
